import { createRootRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { useAuth } from "../components/AuthProvider.js";
import { Users, LayoutDashboard, Mic2, LogOut, Menu, X, MessageSquare } from "lucide-react";
import { useState, useEffect } from "react";

function Layout() {
  const { user, loading, signOut } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="relative">
          <div className="animate-spin h-12 w-12 border-4 border-primary/20 border-t-primary rounded-full" />
          <Users className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-5 w-5 text-primary" />
        </div>
      </div>
    );
  }

  if (!user) return <Outlet />;

  const navItems = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/guests", label: "Guests", icon: Users },
    { to: "/episodes", label: "Episodes", icon: Mic2 },
    { to: "/follow-ups", label: "Follow-ups", icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col md:flex-row">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex w-64 flex-col border-r bg-card sticky top-0 h-screen">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20 group-hover:scale-110 transition-transform">
              <Users className="h-5 w-5" />
            </div>
            <span className="font-black text-2xl tracking-tight italic">Guestflow</span>
          </Link>
        </div>

        <nav className="flex-1 px-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to));
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl font-bold transition-all ${
                  isActive 
                    ? "bg-primary text-primary-foreground shadow-md shadow-primary/10" 
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <item.icon className={`h-5 w-5 ${isActive ? "text-primary-foreground" : "text-primary/60"}`} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t">
          <div className="p-4 rounded-2xl bg-muted/50 border border-border/50">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                {user.email?.[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold truncate">{user.email}</p>
                <p className="text-xs text-muted-foreground">Pro Plan</p>
              </div>
            </div>
            <button 
              onClick={signOut} 
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden border-b bg-card px-4 h-16 flex items-center justify-between sticky top-0 z-50">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Users className="h-4 w-4" />
          </div>
          <span className="font-black text-lg tracking-tight italic">Guestflow</span>
        </Link>
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg bg-muted text-foreground"
        >
          {isMobileMenuOpen ? <X /> : <Menu />}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden bg-background/95 backdrop-blur-sm pt-20 px-4 animate-in fade-in slide-in-from-top-4">
          <nav className="space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center gap-4 p-4 rounded-2xl bg-card border font-bold text-lg"
              >
                <item.icon className="h-6 w-6 text-primary" />
                {item.label}
              </Link>
            ))}
            <button 
              onClick={signOut}
              className="w-full flex items-center gap-4 p-4 rounded-2xl bg-destructive/5 text-destructive font-bold text-lg border border-destructive/10"
            >
              <LogOut className="h-6 w-6" />
              Sign Out
            </button>
          </nav>
        </div>
      )}

      {/* Main Content Area */}
      <main className="flex-1 min-w-0 md:h-screen overflow-y-auto bg-background/50">
        <div className="max-w-6xl mx-auto p-4 md:p-8 lg:p-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export const Route = createRootRoute({ component: Layout });
